// maths.portll.net — LIGHT/DARK theme hook (Wave 3). Single source of truth for the colour scheme,
// fanning ONE choice out to BOTH render systems that must move in lockstep:
//   1. the React DOM shell  → document.documentElement.dataset.theme drives styles/global.css
//   2. the canvas engine    → window.DNF.theme.setScheme(...) mutates DNF.theme.T in place
//
// Persistence: localStorage 'mp_theme' (matches the tt_* convention). Stored value is 'light' or
// 'dark'; ABSENT means "System" → we resolve from prefers-color-scheme and live-follow the OS while
// no explicit choice is stored. Importing this module applies the resolved scheme immediately and
// registers the OS listener once, so first paint is correct on every route.
import { useState, useEffect, useCallback } from "react";

export const THEME_KEY = "mp_theme";

const hasWindow = typeof window !== "undefined";
const hasDoc = typeof document !== "undefined";

function osPrefersDark() {
  return hasWindow && window.matchMedia
    ? window.matchMedia("(prefers-color-scheme: dark)").matches
    : true; // engine default is dark
}

function readStored() {
  try {
    const v = hasWindow && window.localStorage ? window.localStorage.getItem(THEME_KEY) : null;
    return v === "light" || v === "dark" ? v : null;
  } catch {
    return null;
  }
}

// The user-facing preference: 'system' | 'light' | 'dark'.
export function getChoice() {
  return readStored() || "system";
}

// The concrete scheme to render right now: 'light' | 'dark'.
export function resolve() {
  return readStored() || (osPrefersDark() ? "dark" : "light");
}

// Fan the resolved mode out to the DOM shell AND the canvas engine.
export function apply(mode) {
  const m = mode === "light" ? "light" : "dark";
  if (hasDoc) document.documentElement.dataset.theme = m;
  // engine may not be loaded yet on non-game routes — optional-chain it (game pages also call
  // setScheme explicitly right after /game.js loads, so first paint there is never a dark flash).
  try {
    window.DNF && window.DNF.theme && window.DNF.theme.setScheme && window.DNF.theme.setScheme(m);
  } catch {
    /* engine not ready — no-op */
  }
  return m;
}

// Persist (or clear for 'system') then re-apply from the fresh resolution.
export function onToggle(choice) {
  try {
    if (hasWindow && window.localStorage) {
      if (choice === "system") window.localStorage.removeItem(THEME_KEY);
      else window.localStorage.setItem(THEME_KEY, choice === "light" ? "light" : "dark");
    }
  } catch {
    /* storage unavailable — still apply for this session */
  }
  return apply(resolve());
}

// Apply immediately + live-follow the OS while no explicit choice is stored. Registered once.
let _initialised = false;
export function initTheme() {
  if (!hasWindow) return;
  apply(resolve());
  if (_initialised) return;
  _initialised = true;
  if (window.matchMedia) {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      if (!readStored()) apply(resolve()); // only follow the OS while unset
    };
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else if (mq.addListener) mq.addListener(onChange); // older Safari
  }
}

// React binding for the Settings control. Returns [choice, setChoice] where choice is the
// user-facing 'system' | 'light' | 'dark'.
export function useThemePref() {
  const [choice, setChoiceState] = useState(() => getChoice());
  useEffect(() => {
    // reconcile in case another tab/route changed it before this mounted
    setChoiceState(getChoice());
  }, []);
  const setChoice = useCallback((next) => {
    onToggle(next);
    setChoiceState(next);
  }, []);
  return [choice, setChoice];
}

// Apply on import so the DOM chrome + canvas are correct from first paint on every route.
initTheme();
